<template>
    <div>
        <div v-if="processing">
            <LoadingSpinner />
        </div>
        <div v-else class="pt-3">
            <div class="row">
                <!-- SIDEBAR GAUCHE -->
                <div class="col-12 col-lg-4 mb-4">
                    <div class="contract-sidebar">
                        <h5 class="contract-num">#{{ contract.contract_num }}</h5>
                        
                        <!-- Saison + Jument + Étalon -->
                        <div class="timeline-item">
                            <span class="timeline-dot"></span>
                            <div class="timeline-body">
                                <div v-if="season_label">
                                    <b>{{ $t('monte.saison') }} :</b> {{ season_label }}
                                </div>
                                <div v-if="contract.last_avenant && contract.last_avenant.horse">
                                    <b>{{ $t('monte.jument') }} :</b>
                                    {{ contract.last_avenant.horse.horse_nom }}
                                </div>
                                <div v-if="contract.contract_config && contract.contract_config.horse">
                                    <b>{{ $t('monte.etalon') }} :</b>
                                    
                                    {{ contract.contract_config.horse.horse_nom }}
                                    
                                </div>
                            </div>
                        </div>
                        
                        <!-- Lieu de monte -->
                        <div class="timeline-item" v-if="contract.last_avenant && contract.last_avenant.cmep">
                            <span class="timeline-dot"></span>
                            <div class="timeline-body">
                                <div><b>{{ $t('monte.cmep') }} :</b></div>
                                <div>{{ contract.last_avenant.cmep.tiers_rs }}</div>
                                <div v-if="contract.last_avenant.cmep.tiers_address1">{{ contract.last_avenant.cmep.tiers_address1 }}</div>
                                <div v-if="contract.last_avenant.cmep.tiers_address2">{{ contract.last_avenant.cmep.tiers_address2 }}</div>
                                <div>{{ contract.last_avenant.cmep.tiers_postalcode }} {{ contract.last_avenant.cmep.tiers_town }}</div>
                                <div v-if="contract.last_avenant.cmep.tiers_country">{{ getCountryLabel(contract.last_avenant.cmep.tiers_country) }}</div>
                            </div>
                        </div>
                        
                        <!-- Condition de monte -->
                        <div class="timeline-item">
                            <span class="timeline-dot"></span>
                            <div class="timeline-body">
                                <div><b>{{ $t('monte.condition_monte') }} :</b></div>
                                <div v-if="type_monte_label">{{ type_monte_label }}</div>
                                <div v-if="contract.contract_paillette > 0 && showPaillette">{{ contract.contract_paillette }} {{ $t('monte.paillettes') }}</div>
                                <a href="#" class="text-primary" @click.prevent="quickPreview"><font-awesome-icon :icon="['fal', 'eye']" class="mr-1" />{{ $t('monte.preview_contrat') }}</a>
                            </div>
                        </div>
                        
                        <!-- Facturation -->
                        <div class="timeline-item" v-if="contract.tiers">
                            <span class="timeline-dot"></span>
                            <div class="timeline-body">
                                <div><b>{{ $t('monte.facturation') }} :</b></div>
                                <div>{{ contract.tiers.tiers_rs || (contract.tiers.tiers_firstname + ' ' + contract.tiers.tiers_lastname) }}</div>
                                <div v-if="contract.tiers.tiers_address1">{{ contract.tiers.tiers_address1 }}</div>
                                <div v-if="contract.tiers.tiers_address2">{{ contract.tiers.tiers_address2 }}</div>
                                <div>{{ contract.tiers.tiers_postalcode }} {{ contract.tiers.tiers_town }}</div>
                                <div v-if="contract.tiers.tiers_country">{{ getCountryLabel(contract.tiers.tiers_country) }}</div>
                            </div>
                        </div>
                        
                        <!-- Articles / Frais -->
                        <div class="timeline-item" v-if="articles.length > 0">
                            <span class="timeline-dot"></span>
                            <div class="timeline-body">
                                <div v-for="(article, idx) in articles" :key="idx">
                                    <b>{{ article.label }} :</b>
                                    <span v-if="article.price_original" class="text-muted mr-1"><s>{{ article.price_original }}</s></span>
                                    <span>{{ article.price }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- PANNEAU PRINCIPAL DROIT -->
                <div class="col-12 col-lg-8">
                    <!-- En-tête -->
                    <div class="d-flex align-items-center flex-wrap mb-3">
                        <h3 class="mb-0 mr-3 d-none d-sm-block">
                            <font-awesome-icon :icon="['fal', 'mars']" class="mr-1" />
                            {{ $t('monte.contrat') }} #{{ contract.contract_num }}
                        </h3>
                        <button type="button" class="btn btn-primary mt-2 mt-sm-0 mx-auto ml-sm-auto mr-sm-0" @click="openDeclarerModal()">
                            {{ $t('monte.declarer_saillie_dg_poulinage') }}
                            <font-awesome-icon :icon="['fal', 'plus']" class="ml-1" />
                        </button>
                    </div>
                    
                    <!-- Suivi -->
                    <div v-if="suivis.length > 0" class="mb-4">
                        <!-- Desktop : table -->
                        <table class="table d-none d-lg-table">
                            <colgroup><col style="width:33.33%"><col style="width:66.66%"></colgroup>
                            <thead>
                                <tr>
                                    <th>{{ $t('monte.date') }}</th>
                                    <th>{{ $t('monte.suivi') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in suivis" :key="idx">
                                    <td>{{ item.date }}</td>
                                    <td>{{ item.label }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <!-- Mobile : cards -->
                        <div class="d-lg-none">
                            <div v-for="(item, idx) in suivis" :key="'m-suivi-' + idx" class="border-bottom py-2">
                                <small class="text-muted d-block">{{ item.date }}</small>
                                <span>{{ item.label }}</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Documents associés -->
                    <h5>{{ $t('monte.documents_associes_contrat') }}</h5>
                    <!-- Desktop : table -->
                    <table class="table mb-4 d-none d-lg-table" style="table-layout:fixed">
                        <colgroup><col style="width:33.33%"><col style="width:33.33%"><col style="width:33.33%"></colgroup>
                        <thead>
                            <tr>
                                <th>{{ $t('monte.date') }}</th>
                                <th>{{ $t('monte.document') }}</th>
                                <th class="text-center">{{ $t('monte.action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(doc, idx) in documents" :key="idx">
                                <td>{{ (doc.url || doc.action) ? doc.date : '' }}</td>
                                <td class="text-truncate">{{ $t(doc.label_key) }}</td>
                                <td class="text-center">
                                    <a v-if="doc.url" href="#" class="text-primary" @click.prevent="downloadFile(doc.url, doc.label_key)">
                                        <font-awesome-icon :icon="['fal', 'download']" class="mr-1" />{{ $t('monte.telechargement') }}
                                    </a>
                                    <a v-else-if="doc.action" href="#" class="text-primary" @click.prevent="doc.action()">
                                        <font-awesome-icon :icon="['fal', 'download']" class="mr-1" />{{ $t('monte.telechargement') }}
                                    </a>
                                    <span v-else class="text-muted"><font-awesome-icon :icon="['fal', 'clock']" class="mr-1" />{{ $t('monte.bientot_dispo') }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <!-- Mobile : cards -->
                    <div class="d-lg-none mb-4">
                        <div v-for="(doc, idx) in documents" :key="'m-doc-' + idx" class="d-flex align-items-center border-bottom py-2">
                            <div class="flex-fill min-w-0 overflow-hidden">
                                <div class="text-truncate">{{ $t(doc.label_key) }}</div>
                                <small v-if="doc.url || doc.action" class="text-muted">{{ doc.date }}</small>
                            </div>
                            <div class="flex-shrink-0 ml-2">
                                <a v-if="doc.url" href="#" class="text-primary" @click.prevent="downloadFile(doc.url, doc.label_key)">
                                    <font-awesome-icon :icon="['fal', 'download']" />
                                </a>
                                <a v-else-if="doc.action" href="#" class="text-primary" @click.prevent="doc.action()">
                                    <font-awesome-icon :icon="['fal', 'download']" />
                                </a>
                                <span v-else class="text-muted"><font-awesome-icon :icon="['fal', 'clock']" /></span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Factures associées -->
                    <h5>{{ $t('monte.factures_associees_contrat') }}</h5>
                    <!-- Desktop : table -->
                    <table class="table mb-4 d-none d-lg-table" style="table-layout:fixed">
                        <colgroup><col style="width:33.33%"><col style="width:33.33%"><col style="width:33.33%"></colgroup>
                        <thead>
                            <tr>
                                <th class="text-truncate">#</th>
                                <th class="text-truncate text-right">{{ $t('monte.montant_ttc_court') }}</th>
                                <th class="text-truncate text-center">{{ $t('monte.action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="invoice in invoices" :key="invoice.invoice_id">
                                <td>#{{ invoice.invoice_num }}</td>
                                <td class="text-right">{{ invoice.invoice_ttc_format }}</td>
                                <td class="text-center">
                                    <a v-if="invoice.invoice_balance > 0" href="#" class="text-primary" @click.prevent="prepareContractInvoice(invoice)">
                                        <font-awesome-icon :icon="['fal', 'credit-card']" class="mr-1" />{{ $t('monte.payer') }} {{ invoice.invoice_balance_format }}
                                    </a>
                                    <br v-if="invoice.invoice_balance > 0" />
                                    <a href="#" class="text-primary" @click.prevent="downloadInvoice(invoice)">
                                        <font-awesome-icon :icon="['fal', 'download']" class="mr-1" />{{ $t('monte.telechargement') }}
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <!-- Mobile : cards -->
                    <div class="d-lg-none mb-4">
                        <div v-for="invoice in invoices" :key="'m-inv-' + invoice.invoice_id" class="d-flex align-items-center border-bottom py-2">
                            <div class="flex-fill min-w-0 overflow-hidden">
                                <div class="font-weight-bold">#{{ invoice.invoice_num }}</div>
                                <div class="text-muted">{{ invoice.invoice_ttc_format }}</div>
                            </div>
                            <div class="flex-shrink-0 ml-2 text-right">
                                <a v-if="invoice.invoice_balance > 0" href="#" class="btn btn-sm btn-primary mb-1 d-block" @click.prevent="prepareContractInvoice(invoice)">
                                    <font-awesome-icon :icon="['fal', 'credit-card']" class="mr-1" />{{ $t('monte.payer') }} {{ invoice.invoice_balance_format }}
                                </a>
                                <a href="#" class="text-primary" @click.prevent="downloadInvoice(invoice)">
                                    <font-awesome-icon :icon="['fal', 'download']" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Modal déclarer saillie / DG / poulinage -->
        <b-modal ref="modalDeclarer" hide-footer size="lg" @hidden="resetDeclarer">
            <template v-slot:modal-title>
                <span class="d-flex align-items-center">
                    <font-awesome-icon :icon="['fal', 'horse']" class="mr-2 text-primary" style="font-size:1.4rem" />
                    <strong class="text-uppercase">
                        {{ contract.last_avenant && contract.last_avenant.horse ? contract.last_avenant.horse.horse_nom : '' }}
                        <span v-if="contract.contract_config && contract.contract_config.horse"> &amp; {{ contract.contract_config.horse.horse_nom }}</span>
                        <span v-if="season_label"> {{ season_label }}</span>
                    </strong>
                </span>
            </template>

            <!-- Step 1 : choix -->
            <template v-if="declarer_step === null">
                <p class="font-weight-bold mb-3">{{ $t('monte.que_souhaitez_vous_declarer') }}</p>
                <div class="d-flex flex-wrap" style="gap:0.75rem">
                    <button type="button" class="declarer-card flex-fill" @click="declarer_step = 'saillie'">
                        {{ $t('monte.declarer_une_saillie') }}
                    </button>
                    <button type="button" class="declarer-card flex-fill" @click="declarer_step = 'dg'">
                        {{ $t('monte.declarer_un_dg') }}
                    </button>
                    <button type="button" class="declarer-card flex-fill" @click="declarer_step = 'avortement'">
                        {{ $t('monte.declarer_un_avortement') }}
                    </button>
                    <button type="button" class="declarer-card flex-fill" @click="declarer_step = 'poulinage'">
                        {{ $t('monte.declarer_un_poulinage') }}
                    </button>
                    <button type="button" class="declarer-card flex-fill" @click="declarer_step = 'commentaire'">
                        {{ $t('monte.declarer_un_commentaire') }}
                    </button>
                </div>
            </template>

            <!-- Step 2 : formulaire saillie -->
            <template v-else-if="declarer_step === 'saillie'">
     
                <h6 class="font-weight-bold mb-4">{{ $t('monte.formulaire_saillie_titre') }}</h6>

                <div class="form-group">
                    <label class="font-weight-bold">{{ $t('monte.date') }} <span class="text-danger">*</span></label>
                    <e-datepicker v-model="saillie_date" mode="date" :min-date="saillie_min_date" :max-date="saillie_max_date" />
                </div>

                <div class="form-group">
                    <label class="font-weight-bold">{{ $t('monte.type_monte') }}</label>
                    <b-form-select
                        v-model="saillie_type_id"
                        :options="actes_type_saillie_options"
                        :disabled="actes_type_saillie_loading || saillie_submitting"
                    >
                        <template #first>
                    <b-form-select-option :value="null" disabled>{{ actes_type_saillie_loading ? $t('global.chargement') : $t('monte.type_monte_placeholder') }}</b-form-select-option>
                        </template>
                    </b-form-select>
                </div>

                <div class="d-flex justify-content-end mt-4">
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="!saillie_date || !saillie_type_id || saillie_submitting"
                        @click="soumettreSaillie()"
                    >
                        <font-awesome-icon v-if="saillie_submitting" :icon="['fas', 'spinner']" pulse class="mr-1" />
                        {{ $t('monte.soumettre_saillie') }} <font-awesome-icon v-if="!saillie_submitting" :icon="['fal', 'long-arrow-right']" class="ml-1" />
                    </button>
                </div>
            </template>

            <!-- Step Poulinage : formulaire -->
            <template v-else-if="declarer_step === 'poulinage'">

                <h6 class="font-weight-bold mb-4">{{ $t('monte.formulaire_poulinage_titre') }}</h6>

                <div class="form-group">
                    <label class="font-weight-bold">{{ $t('monte.date') }} <span class="text-danger">*</span></label>
                    <e-datepicker v-model="poulinage_date" mode="date" :min-date="poulinage_min_date" :max-date="poulinage_max_date" />
                </div>

                <div class="form-group">
                    <label class="font-weight-bold">{{ $t('monte.resultat_poulinage') }} <span class="text-danger">*</span></label>
                    <b-form-select v-model="poulinage_resultat" :disabled="poulinage_submitting || poulinage_result_options_loading">
                        <template #first>
                            <b-form-select-option :value="null" disabled>{{ poulinage_result_options_loading ? $t('global.chargement') : $t('monte.resultat_poulinage_placeholder') }}</b-form-select-option>
                        </template>
                        <b-form-select-option v-for="opt in poulinage_result_options" :key="opt" :value="opt">{{ $t('reponses.' + opt) }}</b-form-select-option>
                    </b-form-select>
                </div>

                <div class="d-flex justify-content-end mt-4">
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="!poulinage_date || !poulinage_resultat || poulinage_submitting"
                        @click="soumettrePoulinage()"
                    >
                        <font-awesome-icon v-if="poulinage_submitting" :icon="['fas', 'spinner']" pulse class="mr-1" />
                        {{ $t('monte.soumettre_poulinage') }} <font-awesome-icon v-if="!poulinage_submitting" :icon="['fal', 'long-arrow-right']" class="ml-1" />
                    </button>
                </div>
            </template>

            <!-- Step : succès commun -->
            <template v-else-if="declarer_step === 'success'">
                <div class="text-center py-4">
                    <font-awesome-icon :icon="['far', 'check-circle']" class="text-success mb-3" style="font-size:3rem" />
                    <p class="mb-0" v-html="$t('monte.declaration_succes')"></p>
                </div>
            </template>

            <!-- Step DG : formulaire -->
            <template v-else-if="declarer_step === 'dg'">

                <h6 class="font-weight-bold mb-4">{{ $t('monte.formulaire_dg_titre') }}</h6>

                <div class="form-group">
                    <label class="font-weight-bold">{{ $t('monte.date') }} <span class="text-danger">*</span></label>
                    <e-datepicker v-model="dg_date" mode="date" :min-date="saillie_min_date" :max-date="saillie_max_date" />
                </div>

                <div class="form-group">
                    <label class="font-weight-bold">{{ $t('monte.resultat_dg') }} <span class="text-danger">*</span></label>
                    <b-form-select v-model="dg_resultat" :disabled="dg_submitting || dg_result_options_loading">
                        <template #first>
                            <b-form-select-option :value="null" disabled>{{ dg_result_options_loading ? $t('global.chargement') : $t('monte.resultat_dg_placeholder') }}</b-form-select-option>
                        </template>
                        <b-form-select-option v-for="opt in dg_result_options" :key="opt" :value="opt">{{ $t('reponses.' + opt) }}</b-form-select-option>
                    </b-form-select>
                </div>

                <div class="d-flex justify-content-between mt-4">
               
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="!dg_date || !dg_resultat || dg_submitting"
                        @click="soumettreDG()"
                    >
                        <font-awesome-icon v-if="dg_submitting" :icon="['fas', 'spinner']" pulse class="mr-1" />
                        {{ $t('monte.soumettre_dg') }} <font-awesome-icon v-if="!dg_submitting" :icon="['fal', 'long-arrow-right']" class="ml-1" />
                    </button>
                </div>
            </template>



            <!-- Step Commentaire : formulaire -->
            <!-- Permet à l'éleveur d'envoyer un message libre à l'étalonnage depuis sa fiche contrat -->
            <template v-else-if="declarer_step === 'commentaire'">

                <h6 class="font-weight-bold mb-4">{{ $t('monte.formulaire_commentaire_titre') }}</h6>

                <div class="form-group">
                    <label class="font-weight-bold">{{ $t('monte.commentaire_label') }}</label>
                    <textarea
                        v-model="commentaire_text"
                        class="form-control"
                        rows="5"
                        :placeholder="$t('monte.commentaire_placeholder')"
                        :disabled="commentaire_submitting"
                    />
                </div>

                <div class="d-flex justify-content-end mt-4">
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="!commentaire_text || !commentaire_text.trim() || commentaire_submitting"
                        @click="soumettreCommentaire()"
                    >
                        <font-awesome-icon v-if="commentaire_submitting" :icon="['fas', 'spinner']" pulse class="mr-1" />
                        {{ $t('monte.soumettre_commentaire') }} <font-awesome-icon v-if="!commentaire_submitting" :icon="['fal', 'long-arrow-right']" class="ml-1" />
                    </button>
                </div>
            </template>

            <!-- Step Avortement : formulaire -->
            <template v-else-if="declarer_step === 'avortement'">

                <h6 class="font-weight-bold mb-4">{{ $t('monte.formulaire_avortement_titre') }}</h6>

                <div class="form-group">
                    <label class="font-weight-bold">{{ $t('monte.date') }} <span class="text-danger">*</span></label>
                    <!-- Date par défaut : aujourd'hui si dans la saison, sinon dernier acte de la saison, sinon dernier jour de la saison (avortement_suggested_date) -->
                    <e-datepicker v-model="avortement_date" mode="date" :min-date="saillie_min_date" :max-date="saillie_max_date" />
                </div>

                <div class="d-flex justify-content-end mt-4">
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="!avortement_date || avortement_submitting"
                        @click="soumettreAvortement()"
                    >
                        <font-awesome-icon v-if="avortement_submitting" :icon="['fas', 'spinner']" pulse class="mr-1" />
                        {{ $t('monte.soumettre_avortement') }} <font-awesome-icon v-if="!avortement_submitting" :icon="['fal', 'long-arrow-right']" class="ml-1" />
                    </button>
                </div>
            </template>


        </b-modal>

        <b-modal size="xl" ref="modelPreview" hide-footer @hidden="base64 = null">
            <template v-slot:modal-title>
                {{ $t("action.previsualiser") }}
            </template>
            <iframe v-if="base64 != null" :src="'data:application/pdf;base64,' + base64" height="1000px" width="100%" style="position:relative"></iframe>
            <div v-else>
                <LoadingSpinner />
            </div>
        </b-modal>
        
        <b-modal ref="modal" hide-footer>
            <template v-slot:modal-title>
                {{ $t("action.online_paiement") }}
            </template>
            <div v-if="!modal_loaded">
                <LoadingSpinner class="col-12" />
            </div>
            <div>
                <template v-if="marketplace == 'paybox'">
                    <form @submit.prevent="payContract">
                        <b-button class="mt-3" block @click.prevent="payContract">{{ $t("marketplace.payer") }}</b-button>
                    </form>
                    <!-- Formulaire caché pour redirection vers Paybox -->
                    <form method="POST" :action="paybox_infos.url" id="redirectForm" class="d-none">
                        <template v-for="(value, key) in paybox_infos">
                            <input v-if="key !== 'url'" :key="key" type="hidden" :name="key" :value="value">
                        </template>
                        <button id="button_go_paybox" onclick="">{{ $t('global.valider') }}</button>
                    </form>
                </template>
                <template v-if="marketplace == 'sogecommerce'">
                    <form @submit.prevent="payContract">
                        <b-button class="mt-3" block @click.prevent="payContract">{{ $t("marketplace.payer") }}</b-button>
                    </form>
                    <!-- Formulaire caché pour redirection vers Sogecommerce -->
                    <form method="POST" :action="sogecommerce_infos.url" id="redirectForm" class="d-none">
                        <template v-for="(value, key) in sogecommerce_infos">
                            <input v-if="key !== 'url'" :key="key" type="hidden" :name="key" :value="value">
                        </template>
                        <button id="button_go_sogecommerce" onclick="">{{ $t('global.valider') }}</button>
                    </form>
                </template>
                <template v-else-if="marketplace == 'payplug'">
                    <form @submit.prevent="payContract">
                        <b-button class="mt-3" block @click.prevent="payContract">{{ $t("marketplace.payer") }}</b-button>
                    </form>
                </template>
                <template v-else-if="marketplace == 'cawl'">
                    <form @submit.prevent="payContract">
                        <b-button class="mt-3" block @click.prevent="payContract">{{ $t("marketplace.payer") }}</b-button>
                    </form>
                </template>
            </div>
        </b-modal>
        
        <b-modal ref="modalSignature" hide-footer>
            <template v-slot:modal-title>
                {{ $t("monte.lien_signature") }}
            </template>
            <div class="text-center">
                <a :href="signature_link" class="btn btn-primary" target="_blank"><font-awesome-icon :icon="['far', 'pencil-alt']"/> {{ $t('monte.cliquer_ici_signature') }}</a>
            </div>
        </b-modal>
    </div>
</template>

<script type="text/javascript">
import Vue from 'vue'
import ContractMixin from "@/mixins/Contract.js"
import InvoiceMixin from "@/mixins/Invoice.js"
import Common from '@/assets/js/common.js'
import Marketplace from '@/mixins/Marketplace.js'
import UserMixin from "@/mixins/User.js"
import Countries from '@/assets/lang/countries/countries'

export default {
    name: 'ContractFicheHorse',
    mixins: [ContractMixin, InvoiceMixin, Marketplace, UserMixin],
    props: ['horse'],
    data () {
        return {
            contract: {},
            contract_id: 0,
            processing: true,
            base64: null,
            invoices: [],
            total: 0,
            total_int: 0,
            modal_loaded: false,
            marketplace: 'mangopay',
            paybox_infos: {},
            sogecommerce_infos: {},
            signature_link: '',
            processing_signature: false,
            season_label: '',
            type_monte_label: '',
            type_monte_type: null,
            articles: [],
            suivis: [],
            documents: [],
            // Declarer modal
            declarer_step: null,
            saillie_date: new Date(),
            saillie_type_id: null,
            saillie_submitting: false,
            actes_type_saillie_list: [],
            actes_type_saillie_loading: false,
            dg_date: new Date(),
            dg_resultat: null,
            dg_submitting: false,
            dg_result_options: [],
            dg_result_options_loading: false,
            avortement_date: new Date(),
            avortement_submitting: false,
            poulinage_date: new Date(),
            poulinage_resultat: null,
            poulinage_submitting: false,
            poulinage_result_options: [],
            poulinage_result_options_loading: false,
            /** Texte saisi par l'éleveur dans le formulaire de commentaire libre */
            commentaire_text: '',
            /** Vrai pendant l'envoi du commentaire (désactive le bouton et affiche le spinner) */
            commentaire_submitting: false,
        }
    },
    
    activated() {
        this.init_component()
    },
    
    methods: {
        /**
         * Charge le contrat complet et initialise toutes les données locales :
         * saison, label type de monte, articles/frais (avec formatage TTC),
         * tableau de suivi des actes, factures et liste des documents associés.
         */
        async init_component() {
            this.processing = true
            this.contract_id = this.$route.params.contract_id
            
            this.contract = await this.getContract(this.contract_id)
            
            // Saison
            if(this.contract.contract_config && this.contract.contract_config.season) {
                this.season_label = this.contract.contract_config.season.season_label
            }
            
            // Type de monte (inclus dans last_avenant.type_monte via eager loading)
            const tm = this.contract.last_avenant ? this.contract.last_avenant.type_monte : null
            if(tm) {
                this.type_monte_label = tm.contractconfigtypemonte_label || (tm.type_monte && tm.type_monte.contracttypemonte_label) || ''
                this.type_monte_type = tm.type_monte ? tm.type_monte.contracttypemonte_type : null
            }
            
            // Articles / frais
            const avenant_articles = (this.contract.last_avenant && this.contract.last_avenant.avenant_articles) ? this.contract.last_avenant.avenant_articles : []
            const processedArticles = []
            for(const art of avenant_articles) {
                const label = art.condition
                ? this.getTrad(art.condition.conditions.contractconditions_label)
                : (art.type_condition ? this.getTrad(art.type_condition.contractconditions_label) : art.avenantarticles_label || '')
                const price = await Common.priceFormat(art.avenantarticles_ht / 100, null, true)
                const entry = { label, price }
                if(art.avenantarticles_ht_promo != null && art.avenantarticles_ht_promo !== art.avenantarticles_ht) {
                    entry.price_original = await Common.priceFormat(art.avenantarticles_ht / 100, null, true)
                    entry.price = await Common.priceFormat(art.avenantarticles_ht_promo / 100, null, true)
                }
                processedArticles.push(entry)
            }
            this.articles = processedArticles
            
            // Suivi
            const sms = this.contract.season_mare_stallion
            if(sms) {
                const suivisList = []
                if(Array.isArray(sms.actes) && sms.actes.length > 0) {
                    for(const acte of sms.actes) {
                        let label = acte.acte_type_code ? Vue.i18n.translate('gynecologie.actes_type.' + acte.acte_type_code) : ''
                        if(acte.dg_reponse) {
                            label += ' — ' + acte.dg_reponse
                        }
                        suivisList.push({ date: this.formatDate(acte.actes_date), label })
                    }
                } else if(sms.seasonmarestallion_status) {
                    suivisList.push({ date: '', label: sms.seasonmarestallion_status })
                }
                this.suivis = suivisList
            }
            
            // Invoices
            let total = 0
            const invoices = await this.loadInvoiceContract(this.contract_id)
            if(invoices && invoices.length > 0) {
                for(let i in invoices) {
                    invoices[i].invoice_balance_format = await Common.priceFormat(invoices[i].invoice_balance/100, invoices[i].tier.tiers_currency, true)
                    invoices[i].invoice_ttc_format = await Common.priceFormat(invoices[i].invoice_ttc/100, invoices[i].tier.tiers_currency, true)
                    total += invoices[i].invoice_balance/100
                }
                this.total_int = total
                total = await Common.priceFormat(total, invoices[0].tier.tiers_currency, true)
            }
            this.total = total
            this.invoices = invoices || []
            
            // Documents
            const contractDate = this.contract.last_avenant
            ? (this.contract.last_avenant.avenant_date_signature || this.contract.last_avenant.avenant_date_validation || this.contract.last_avenant.avenant_date_creation || '')
            : ''
            const docs = []
            docs.push({
                date: this.formatDate(contractDate),
                label_key: 'monte.contrat_signe',
                url: null,
                action: () => this.downloadContractPdf()
            })
            if(this.contract.document_as !== undefined) {
                docs.push({ date: this.formatDate(contractDate), label_key: 'monte.attestation_saillie', url: this.contract.document_as || null })
            }
            if(this.contract.document_dn !== undefined) {
                docs.push({ date: this.formatDate(contractDate), label_key: 'monte.declaration_naissance', url: this.contract.document_dn || null })
            }
            this.documents = docs
            
            this.processing = false
        },
        
        /**
         * Ouvre la modal de déclaration.
         * Les dates sont pré-remplies avec les dates suggérées selon le contexte du contrat.
         * Les listes de sélection (types saillie, résultats DG, résultats poulinage) sont
         * chargées en lazy depuis l'API pour éviter des appels inutiles.
         */
        async openDeclarerModal() {
            this.saillie_date = this.saillie_suggested_date
            this.dg_date = this.saillie_suggested_date
            this.avortement_date = this.avortement_suggested_date
            this.$refs.modalDeclarer.show()
            this.poulinage_date = this.poulinage_suggested_date
            if(this.actes_type_saillie_list.length === 0) {
                this.actes_type_saillie_loading = true
                this.actes_type_saillie_list = await this.getActeTypesSaillie()
                this.actes_type_saillie_loading = false
            }
            if(this.dg_result_options.length === 0) {
                this.dg_result_options_loading = true
                this.dg_result_options = await this.getDGResultOptions()
                this.dg_result_options_loading = false
            }
            if(this.poulinage_result_options.length === 0) {
                this.poulinage_result_options_loading = true
                this.poulinage_result_options = await this.getPoulinageResultOptions()
                this.poulinage_result_options_loading = false
            }
        },

        /** Appelé via @hidden de la modal – remet tous les champs à zéro et revient à l'étape de choix. */
        resetDeclarer() {
            this.declarer_step = null
            this.saillie_date = this.saillie_suggested_date
            this.saillie_type_id = null
            this.saillie_submitting = false
            this.dg_date = this.saillie_suggested_date
            this.dg_resultat = null
            this.dg_submitting = false
            this.avortement_date = this.avortement_suggested_date
            this.avortement_submitting = false
            this.commentaire_text = ''
            this.commentaire_submitting = false
            this.poulinage_date = this.poulinage_suggested_date
            this.poulinage_resultat = null
            this.poulinage_submitting = false
        },

        /** Envoie la déclaration de saillie via le endpoint unifié POST /acte/declaration. */
        async soumettreSaillie() {
            if (!this.saillie_date || !this.saillie_type_id) return
            this.saillie_submitting = true
            const date = this.saillie_date instanceof Date
                ? this.saillie_date.toISOString().substr(0, 10)
                : this.saillie_date
            const result = await this.declarerActe({
                contract_id:  this.contract_id,
                date,
                type:         'saillie',
                acte_type_id: this.saillie_type_id,
            })
            this.saillie_submitting = false
            if (result) {
                this.declarer_step = 'success'
            } else {
                this.failureToast('monte.declaration_erreur')
            }
        },

        /** Envoie la déclaration de diagnostic de gestation avec son résultat. */
        async soumettreDG() {
            if (!this.dg_date || !this.dg_resultat) return
            this.dg_submitting = true
            const date = this.dg_date instanceof Date
                ? this.dg_date.toISOString().substr(0, 10)
                : this.dg_date
            const result = await this.declarerActe({
                contract_id: this.contract_id,
                date,
                type:        'dg',
                resultat:    this.dg_resultat,
            })
            this.dg_submitting = false
            if (result) {
                this.declarer_step = 'success'
            } else {
                this.failureToast('monte.declaration_erreur')
            }
        },

        /**
         * Envoie un avortement en réutilisant le type d'acte DG :
         * le résultat est forcé à 'Avortée', pas de select à afficher côté utilisateur.
         */
        async soumettreAvortement() {
            if (!this.avortement_date) return
            this.avortement_submitting = true
            const date = this.avortement_date instanceof Date
                ? this.avortement_date.toISOString().substr(0, 10)
                : this.avortement_date
            const result = await this.declarerActe({
                contract_id: this.contract_id,
                date,
                type:        'dg',
                resultat:    'Avortée',
            })
            this.avortement_submitting = false
            if (result) {
                this.declarer_step = 'success'
            } else {
                this.failureToast('monte.declaration_erreur')
            }
        },

        /**
         * Envoie la déclaration de poulinage.
         */
        async soumettrePoulinage() {
            if (!this.poulinage_date || !this.poulinage_resultat) return
            this.poulinage_submitting = true
            const date = this.poulinage_date instanceof Date
                ? this.poulinage_date.toISOString().substr(0, 10)
                : this.poulinage_date
            const result = await this.declarerActe({
                contract_id: this.contract_id,
                date,
                type:        'poulinage',
                resultat:    this.poulinage_resultat,
            })
            this.poulinage_submitting = false
            if (result) {
                this.declarer_step = 'success'
            } else {
                this.failureToast('monte.declaration_erreur')
            }
        },

        /**
         * Envoie un commentaire libre depuis la fiche contrat.
         * Aucun acte n'est créé : l'admin reçoit un mail avec le texte
         * et un bouton pour ouvrir directement le contrat dans Groomy.
         */
        async soumettreCommentaire() {
            if (!this.commentaire_text || !this.commentaire_text.trim()) return
            this.commentaire_submitting = true
            const result = await this.declarerActe({
                contract_id: this.contract_id,
                type:        'commentaire',
                commentaire: this.commentaire_text.trim(),
            })
            this.commentaire_submitting = false
            if (result) {
                this.declarer_step = 'success'
            } else {
                this.failureToast('monte.declaration_erreur')
            }
        },

        goBackToList() {
            this.$router.push({ name: 'ContractMareList', params: { horse_id: this.$route.params.horse_id } })
        },

        getCountryLabel(id) {
            if(!id) return ''
            const labels = Countries[Vue.i18n.locale()]
            return (labels && labels.label && labels.label[id]) || id
        },
        
        formatDate(dateStr) {
            if(!dateStr) return ''
            try {
                const d = new Date(dateStr)
                return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
            } catch(e) {
                return dateStr
            }
        },
        
        downloadFile(uri, name) {
            const link = document.createElement("a")
            link.download = this.getTrad(name)
            link.href = uri
            link.target = '_blank'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        
        async downloadContractPdf() {
            await this.printContract(this.contract_id, false)
        },
        
        async quickPreview() {
            this.$refs.modelPreview.show()
            let retour = await this.printContract(this.contract_id, true)
            if(retour) {
                this.base64 = retour
            }
        },
        
        async downloadInvoice(invoice) {
            await this.getAddressedPdfInvoice([invoice])
        },
        
        async prepareContractInvoice(invoice) {
            let marketplace = await this.getMarketplaceType()
            this.marketplace = marketplace.retour.type
            
            this.modal_loaded = true
            this.contracts_to_pay = [this.contract.contract_id]
            this._invoice_to_pay = invoice
            this.$refs.modal.show()
        },
        
        async prepareContract() {
            let marketplace = await this.getMarketplaceType()
            this.marketplace = marketplace.retour.type
            
            this.modal_loaded = true
            this.contracts_to_pay = [this.contract.contract_id]
            this.$refs.modal.show()
        },
        
        async payContract() {
            let duedates_id = this.invoices.map(invoice => invoice.duedate.filter(duedate => duedate.duedate_balance !== 0).map(duedate => duedate.duedate_id)).flat(1)
            
            if(this.marketplace == 'paybox') {
                let datas_payment = await this.redirectBoutique(duedates_id)
                if(datas_payment != null && datas_payment.retour.url != null) {
                    this.paybox_infos = datas_payment.retour
                    if(this.paybox_infos.PBX_TOTAL == '0') {
                        this.failureToast("toast.contract_montant_zero")
                        this.$refs.modal.hide()
                        return false
                    }
                    setTimeout(() => { document.querySelector("#button_go_paybox").click() }, 0)
                }
            }
            else if(this.marketplace == 'sogecommerce') {
                let datas_payment = await this.redirectBoutique(duedates_id)
                if(datas_payment != null && datas_payment.retour.url != null) {
                    this.sogecommerce_infos = datas_payment.retour
                    if(this.sogecommerce_infos.vads_amount == 0) {
                        this.failureToast("toast.contract_montant_zero")
                        this.$refs.modal.hide()
                        return false
                    }
                    setTimeout(() => { document.querySelector("#button_go_sogecommerce").click() }, 0)
                }
            }
            else if(this.marketplace == 'payplug') {
                let datas_payment = await this.redirectBoutique(duedates_id)
                if(datas_payment != null && datas_payment.retour.url != null) {
                    if(datas_payment.retour.amount == 0) {
                        this.failureToast("toast.contract_montant_zero")
                        this.$refs.modal.hide()
                        return false
                    }
                    location.replace(datas_payment.retour.url)
                }
            }
            else if(this.marketplace == 'cawl') {
                let datas_payment = await this.redirectBoutique(duedates_id)
                if(datas_payment != null && datas_payment.retour.url != null) {
                    if(datas_payment.retour.amount == 0) {
                        this.failureToast("toast.contract_montant_zero")
                        this.$refs.modal.hide()
                        return false
                    }
                    location.replace(datas_payment.retour.url)
                }
            }
        },
        
        async signatureContract() {
            this.processing_signature = true
            const url = await this.getContractSignatureLink(this.contract_id)
            if(url) {
                this.signature_link = url
                this.$refs.modalSignature.show()
            }
            this.processing_signature = false
        }
    },
    
    computed: {
        showPaillette() {
            return ['iac', 'iarr', 'vp'].indexOf(this.type_monte_type) !== -1
        },

        mailto() {
            return 'mailto:' + this.getUserCAMail() + '?subject=' + this.getTrad('monte.mail_subject') + '&body=' + this.getTrad('monte.mail_body')
        },
        
        hasSignatureAccess() {
            return this.$store.state.userAccess.hasSignatureAccess
        },

        actes_type_saillie_options() {
            return this.actes_type_saillie_list.map(t => ({
                value: t.actestype_id,
                text: (t.actestype_labels && t.actestype_labels[this.$i18n.locale()])
                    || (t.actestype_labels && t.actestype_labels['fr'])
                    || t.actestype_label
                    || t.actestype_code
            }))
        },

        /** Borne inférieure du datepicker saillie/DG/avortement = début de saison. */
        saillie_min_date() {
            const start = this.contract && this.contract.contract_config && this.contract.contract_config.season && this.contract.contract_config.season.season_start
            return start ? new Date(start) : null
        },

        /** Borne supérieure du datepicker saillie/DG/avortement = fin de saison. */
        saillie_max_date() {
            const end = this.contract && this.contract.contract_config && this.contract.contract_config.season && this.contract.contract_config.season.season_end
            return end ? new Date(end) : null
        },

        /**
         * Borne inférieure du datepicker poulinage = season_start + 1 an.
         * Un poulinage survient la saison suivante (gestation ~11 mois).
         */
        poulinage_min_date() {
            const start = this.contract && this.contract.contract_config && this.contract.contract_config.season && this.contract.contract_config.season.season_start
            if (!start) return null
            const d = new Date(start)
            d.setFullYear(d.getFullYear() + 1)
            return d
        },

        /** Borne supérieure du datepicker poulinage = season_end + 1 an. */
        poulinage_max_date() {
            const end = this.contract && this.contract.contract_config && this.contract.contract_config.season && this.contract.contract_config.season.season_end
            if (!end) return null
            const d = new Date(end)
            d.setFullYear(d.getFullYear() + 1)
            return d
        },

        /**
         * Date par défaut du datepicker poulinage, selon la priorité :
         * 1. Aujourd'hui si dans la période [season_start+1an, season_end+1an]
         * 2. Lendemain du dernier acte saisi (si ce lendemain est dans la période)
         * 3. Premier jour de la période (season_start + 1 an)
         */
        poulinage_suggested_date() {
            const min = this.poulinage_min_date
            const max = this.poulinage_max_date
            const today = new Date()

            // 1. Si aujourd'hui est dans la période poulinage, utiliser aujourd'hui
            const inRange = (!min || today >= min) && (!max || today <= max)
            if (inRange) return today

            // 2. Sinon, une date supérieure à la date du dernier acte saisi
            const sms = this.contract && this.contract.season_mare_stallion
            if (sms && Array.isArray(sms.actes) && sms.actes.length > 0) {
                const sorted = [...sms.actes]
                    .filter(a => a.actes_date)
                    .sort((a, b) => new Date(b.actes_date) - new Date(a.actes_date))
                if (sorted.length > 0) {
                    const lastActeDate = new Date(sorted[0].actes_date)
                    const next = new Date(lastActeDate)
                    next.setDate(next.getDate() + 1)
                    if ((!min || next >= min) && (!max || next <= max)) return next
                }
            }

            // 3. Fallback : premier jour de la période poulinage
            return min ? new Date(min) : today
        },

        /**
         * Date par défaut du datepicker avortement, selon la priorité :
         * 1. Aujourd'hui si elle est dans la période de la saison [season_start, season_end]
         * 2. Sinon, la date du dernier acte enregistré sur ce Season-Mare-Stallion
         * 3. Sinon, le dernier jour de la saison (season_end)
         */
        avortement_suggested_date() {
            const season = this.contract && this.contract.contract_config && this.contract.contract_config.season
            const min = this.saillie_min_date
            const max = this.saillie_max_date
            const today = new Date()

            // 1. Aujourd'hui si dans la saison
            const todayInSeason = (!min || today >= min) && (!max || today <= max)
            if (todayInSeason) return today

            // 2. Date du dernier acte enregistré sur ce SMS
            const sms = this.contract && this.contract.season_mare_stallion
            if (sms && Array.isArray(sms.actes) && sms.actes.length > 0) {
                const sorted = [...sms.actes]
                    .filter(a => a.actes_date)
                    .sort((a, b) => new Date(b.actes_date) - new Date(a.actes_date))
                if (sorted.length > 0) return new Date(sorted[0].actes_date)
            }

            // 3. Fallback : dernier jour de la saison
            return max ? new Date(max) : today
        },

        /**
         * Date par défaut du datepicker saillie/DG, selon la priorité :
         * 1. Date du dernier acte existant sur ce Season-Mare-Stallion
         * 2. Date de validation/signature du contrat si elle est dans la saison
         * 3. Aujourd'hui
         */
        saillie_suggested_date() {
            const season = this.contract && this.contract.contract_config && this.contract.contract_config.season
            const sms = this.contract && this.contract.season_mare_stallion

            // 1. Date du dernier acte saisi sur ce SMS
            if (sms && Array.isArray(sms.actes) && sms.actes.length > 0) {
                const sorted = [...sms.actes]
                    .filter(a => a.actes_date)
                    .sort((a, b) => new Date(b.actes_date) - new Date(a.actes_date))
                if (sorted.length > 0) return new Date(sorted[0].actes_date)
            }

            // 2. Date de validation du contrat si dans la saison
            const avenant = this.contract && this.contract.last_avenant
            const contractDateStr = avenant && (avenant.avenant_date_signature || avenant.avenant_date_validation || avenant.avenant_date_creation)
            if (contractDateStr && season) {
                const contractDate = new Date(contractDateStr)
                const seasonStart = season.season_start ? new Date(season.season_start) : null
                const seasonEnd   = season.season_end   ? new Date(season.season_end)   : null
                const inSeason = (!seasonStart || contractDate >= seasonStart) && (!seasonEnd || contractDate <= seasonEnd)
                if (inSeason) return contractDate
            }

            // 3. Fallback : aujourd'hui
            return new Date()
        },
    },
    
    components: {
        LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
    }
}
</script>

